import { useMemo, type VoidFunctionComponent } from 'react';
import Subnav from '../../generic/Subnav';
import { useCanDisplaySecurityOptions } from '../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../generic/ReportGeneration/utils';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { DISABLE_REPORT_UI_REFRESH, FIX_PLAN_REPORT } from '../../../../shared/featureFlags/flagNames';
import { isFreeTier } from '../../../../shared/subscriptions';
import { window } from '../../../globals';

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag.
 */
const useEnabledReportTypes = () => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isFixPlanReportsEnabled = useIsFeatureEnabled(FIX_PLAN_REPORT);

  return useMemo(() => {
    const excludedReports: string[] = [];
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.push(ReportCategories.Vulnerability);
    }
    if (!isFixPlanReportsEnabled) {
      excludedReports.push(ReportCategories.FixPlan);
    }

    return Object.entries(ReportCategoryTitles).filter(([key]) => !excludedReports.includes(key));
  }, [isSecurityAndPDFReportsEnabled, isFixPlanReportsEnabled]);
};

type Props = {
  baseRevisionURL: string;
};

/**
 * ProjectSubnavItemReports is the dropdown subnav item for project Reports.
 */
const ProjectSubnavItemReports: VoidFunctionComponent<Props> = ({ baseRevisionURL }) => {
  const isReportUIRefreshEnabled = !useIsFeatureEnabled(DISABLE_REPORT_UI_REFRESH);
  const shouldShowSettings = isReportUIRefreshEnabled && !isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const enabledReportTypes = useEnabledReportTypes();
  return (
    <Subnav.Dropdown to={`${baseRevisionURL}/reports`} navKey="reports">
      <Subnav.Dropdown.RootItem>Reports</Subnav.Dropdown.RootItem>
      {enabledReportTypes.map(([reportType, title]) => (
        <Subnav.Dropdown.Item
          key={`reports/${reportType}`}
          to={`${baseRevisionURL}/reports/${reportType}`}
          navKey={`reports/${reportType}`}
        >
          {title}
        </Subnav.Dropdown.Item>
      ))}
      {shouldShowSettings && (
        <Subnav.Dropdown.Item
          key="reports/settings"
          to={`${baseRevisionURL}/reports/settings`}
          navKey="reports/settings"
        >
          Settings
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ProjectSubnavItemReports;
